import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, watch, onMounted, computed } from 'vue'
import { debounce } from 'lodash'
import PaymentPolicyService from '@/Network/Services/DsPaymentPolicyService'
import type { PaymentPolicy } from '@/Network/Types/Responses/DsPaymentPolicyResponses'
import Network from '@/Network'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import BasePolicyTable from '../BasePolicyTable.vue'
import Legend from '@/components/designSystem/Legend/Legend.vue'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import {
  getPostStationApprovedMessage,
  isPostStationApprovedError
} from '@/modules/company/PostStation/errors'


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    scheduleId: {}
  },
  emits: ['create-policy', 'edit-policy', 'loaded-requirements'],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;





const vigencyLegendItems = [
  { label: 'Em vigência', borderColor: 'border-green-500', bgColor: 'bg-green-100' },
  { label: 'Expirada', borderColor: 'border-red-500', bgColor: 'bg-red-100' },
  { label: 'Futura', borderColor: 'border-blue-500', bgColor: 'bg-blue-100' }
]

const search = ref('')
const policies = ref<PaymentPolicy[]>([])
const isLoading = ref(false)
const loadingSwitches = ref<Set<number>>(new Set())
const service = new PaymentPolicyService()
// `toast` permanece para a mensagem já pronta do guard de Pós-Plantão; os demais
// erros de API passam pelo `notifyError` (rule `error-handling.md`).
const toast = useToast()
const { notifyError } = useErrorToast()

const permissionsStore = usePermissionsStore()

const canCreatePolicy = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.SchedulePaymentPoliciesCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const canEditPolicy = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.SchedulePaymentPoliciesEditing,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const handleCreate = () => {
  emit('create-policy')
}

const handleEditPolicy = (policy: PaymentPolicy) => {
  emit('edit-policy', policy)
}

const fetchPolicies = async () => {
  if (!props.scheduleId) return
  isLoading.value = true
  try {
    const result = await service.listByScheduleId(props.scheduleId, {
      search: search.value?.trim() || undefined
    })

    policies.value = result.data
  } catch (error) {
    policies.value = []
    // Sem isso a aba desenhava "nenhuma política", como se a escala não tivesse nenhuma.
    notifyError(error, 'Não foi possível carregar as políticas de pagamento da escala.')
  } finally {
    isLoading.value = false
  }
}

const handleTogglePolicyStatus = async (id: number, enabled: boolean) => {
  loadingSwitches.value.add(id)
  try {
    enabled
      ? await service.activate(id, props.scheduleId)
      : await service.disable(id, props.scheduleId)
    await fetchPolicies()
  } catch (e) {
    // Trava do Pós-Plantão (RN-07 / RF-12): só toast, porque não há modal onde
    // ancorar banner. O refetch fica DENTRO deste ramo de propósito — nos outros
    // erros o comportamento tem que continuar o de antes (sem refetch), senão a
    // rodada passa a mudar produção mesmo com o Audit Bot desligado.
    // Fica fora do `notifyError` de propósito: o guard já devolve a mensagem
    // curada do domínio, e o funil de `getApiErrorMessage` exibiria o texto cru
    // do 400 no lugar dela.
    if (isPostStationApprovedError(e)) {
      toast.error(getPostStationApprovedMessage(e))
      // O switch já mudou visualmente; sem isso ele fica mentindo na tela.
      await fetchPolicies()
      return
    }

    notifyError(e, 'Não foi possível alterar o status da política de pagamento.')
  } finally {
    loadingSwitches.value.delete(id)
  }
}

const loadRequirements = async () => {
  try {
    const schedule = await Network.schedule.getById(props.scheduleId)

    emit('loaded-requirements', schedule.requiresDoctorPaymentFormation)
  } catch (err) {
    console.error('Erro ao carregar:', err)
  }
}

watch(search, debounce(fetchPolicies, 300))

onMounted(fetchPolicies)

onMounted(() => {
  loadRequirements()
})

__expose({
  fetchPolicies
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BasePolicyTable, {
    search: search.value,
    "onUpdate:search": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
    policies: policies.value,
    "is-loading": isLoading.value,
    "loading-switches": loadingSwitches.value,
    "can-create": canCreatePolicy.value,
    "can-edit": canEditPolicy.value,
    onCreate: handleCreate,
    onEdit: handleEditPolicy,
    onToggleStatus: handleTogglePolicyStatus
  }, {
    legend: _withCtx(() => [
      _createVNode(Legend, {
        items: vigencyLegendItems,
        class: "text-sm text-gray-600"
      })
    ]),
    _: 1
  }, 8, ["search", "policies", "is-loading", "loading-switches", "can-create", "can-edit"]))
}
}

})